/** Id do formato Hentai na API. */
export const HENTAI_FORMATO_ID = 10;

export function isHentaiFormato(formatoId?: number | null): boolean {
    return Number(formatoId) === HENTAI_FORMATO_ID;
}

/** Default do query `formato` na home — alinhado a `HomePage`. */
export const HOME_FORMATO_PADRAO = 5;

/** Formatos de obra — mesma ordem/ids da home (ObrasHome) e API `obras/novos`. */
export const OBRAS_GENEROS = [
    { id: 'comic', label: 'Comic', formato: 5, color: '#f97316' },
    { id: 'manga', label: 'Manga', formato: 13, color: '#22c55e' },
    { id: 'shoujo', label: 'Shoujo', formato: 4, color: '#a855f7' },
    { id: 'yaoi', label: 'Yaoi', formato: 8, color: '#ec4899' },
    { id: 'yuri', label: 'Yuri', formato: 9, color: '#3b82f6' },
    { id: 'hentai', label: 'Hentai', formato: 10, color: '#ef4444' },
    { id: 'english', label: 'English', formato: 1, color: '#3b82f6' },
    { id: 'novel', label: 'Novel', formato: 3, color: '#0ea5e9' },
] as const;

const HOME_FORMATO_LS_KEY = 'mediocre:home_formato_preferido';

function isKnownHomeFormatoId(n: number): boolean {
    return OBRAS_GENEROS.some((g) => g.formato === n);
}

/** Último género escolhido na home (persiste entre visitas e ao tocar «Início» na bottom bar). */
export function readHomePreferredFormato(): number | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(HOME_FORMATO_LS_KEY);
        if (raw == null || raw === '') return null;
        const num = Number(raw);
        if (!Number.isFinite(num) || !isKnownHomeFormatoId(num)) return null;
        return num;
    } catch {
        return null;
    }
}

export function writeHomePreferredFormato(formatoId: number): void {
    if (typeof window === 'undefined') return;
    if (!Number.isFinite(formatoId) || !isKnownHomeFormatoId(formatoId)) return;
    try {
        window.localStorage.setItem(HOME_FORMATO_LS_KEY, String(formatoId));
    } catch {
        /* quota / modo privado */
    }
}

/** Primeiro render da home: `?formato=` válido > preferência guardada > padrão. */
export function resolveInitialHomeFormato(searchParams: URLSearchParams): number {
    const raw = searchParams.get('formato');
    if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n) && isKnownHomeFormatoId(n)) return n;
    }
    return readHomePreferredFormato() ?? HOME_FORMATO_PADRAO;
}

export function formatoIdFromHomeSearch(search: string): number {
    const raw = new URLSearchParams(search).get('formato');
    if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n) && isKnownHomeFormatoId(n)) return n;
    }
    const stored = readHomePreferredFormato();
    if (stored != null) return stored;
    return HOME_FORMATO_PADRAO;
}

/** Fallback do acento (Comic / laranja) quando o id não está em `OBRAS_GENEROS`. */
export const DEFAULT_HOME_ACCENT_HEX = '#f97316';

/** Cor do pill do formato — mesma da home e da bottom bar em `/`. */
export function accentHexForFormato(formatoId: number): string {
    const g = OBRAS_GENEROS.find((x) => x.formato === formatoId);
    return (g?.color as string | undefined) ?? DEFAULT_HOME_ACCENT_HEX;
}

export function hexToRgbTuple(hex: string): [number, number, number] | null {
    const h = hex.replace(/^#/, '').trim();
    if (h.length !== 6) return null;
    const r = Number.parseInt(h.slice(0, 2), 16);
    const g = Number.parseInt(h.slice(2, 4), 16);
    const b = Number.parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return [r, g, b];
}

export function rgbaFromHex(hex: string, alpha: number): string {
    const t = hexToRgbTuple(hex);
    if (!t) return `rgba(249,115,22,${alpha})`;
    return `rgba(${t[0]},${t[1]},${t[2]},${alpha})`;
}

/**
 * Cor da bottom bar: obra em foco (ex. página da obra) > home (`?formato=` ou LS) > preferência LS no resto da app.
 */
export function resolveMobileBottomNavAccentHex(
    pathname: string,
    search: string,
    pageOverlayFormatoId: number | null,
): string {
    if (pageOverlayFormatoId != null && isKnownHomeFormatoId(pageOverlayFormatoId)) {
        return accentHexForFormato(pageOverlayFormatoId);
    }
    if (pathname === '/') {
        return accentHexForFormato(formatoIdFromHomeSearch(search));
    }
    return accentHexForFormato(readHomePreferredFormato() ?? HOME_FORMATO_PADRAO);
}

/** Só em `/` — preferir `resolveMobileBottomNavAccentHex` na bottom bar. */
export function homeBottomNavAccentHex(pathname: string, search: string): string | null {
    if (pathname !== '/') return null;
    return accentHexForFormato(formatoIdFromHomeSearch(search));
}
